import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as d3 from 'd3';
import { Component } from './Component';
import { createLogger } from '../utils/logger';

const log = createLogger('MapView');

export class MapView extends Component {
  private map!: L.Map;
  private g!: SVGGElement;

  render(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'map';
    container.style.cssText = 'width:100%;height:100vh;';
    return container;
  }

  mount(parent: HTMLElement): void {
    super.mount(parent);
    this.initMap();
  }

  private initMap(): void {
    this.map = L.map(this.el).setView([48, -20], 3);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
    }).addTo(this.map);

    // ROADMAP § 8.1: attribute Nominatim geocoding data alongside the default tile credit.
    this.map.attributionControl.addAttribution(
      'Geocoding &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    );

    // Place the SVG directly in the map container (not a Leaflet pane).
    // Sizing to 100%/100% works reliably here because the map container has
    // explicit pixel dimensions. We use latLngToContainerPoint for all
    // coordinate projection — container points are relative to the map div's
    // top-left corner, which exactly matches an absolute-positioned SVG at 0,0.
    // This avoids the layer-space/pixel-origin offset complexity of pane-based overlays.
    const svgSel = d3
      .select(this.map.getContainer())
      .append('svg')
      .attr('class', 'd3-overlay')
      .attr('data-testid', 'map-svg-overlay')
      .attr('aria-hidden', 'true') // decorative — data accessible via BranchPanel/BrickWallPanel
      .style('position', 'absolute')
      .style('top', '0')
      .style('left', '0')
      .style('width', '100%')
      .style('height', '100%')
      .style('z-index', '450')
      .style('pointer-events', 'none')
      .style('overflow', 'visible');

    this.g = d3.select(svgSel.node()!).append('g').attr('class', 'arc-dot-layer').node()!;

    // Allow the SVG overlay (and its arcs) to render outside the map container bounds.
    // Great-circle arcs curve north toward Iceland/Greenland; without this, the Leaflet
    // container's default overflow:hidden clips the arc apex and the far endpoint never
    // reappears. The map is full-viewport so there is nothing visible to clip anyway.
    this.map.getContainer().style.overflow = 'visible';

    // Defer invalidateSize until after the browser layout pass.
    // Without this, Leaflet measures the container at 0×0 (element just appended,
    // no paint yet) and coordinate projection returns wrong values.
    requestAnimationFrame(() => {
      this.map.invalidateSize();
    });

    log.info('MapView mounted — Leaflet + D3 SVG overlay ready');
  }

  /** Project a lat/lng to SVG coordinates (== container-point coordinates). */
  private project(latLng: L.LatLngExpression): L.Point {
    return this.map.latLngToContainerPoint(L.latLng(latLng));
  }

  /**
   * Register a callback to fire whenever the map moves, zooms, or resets.
   * P-08 layers call: this.track(mapView.onViewChanged(() => this.reproject()))
   * Returns an unsubscribe function compatible with Component.track().
   */
  onViewChanged(fn: () => void): () => void {
    this.map.on('moveend', fn);
    this.map.on('zoomend', fn);
    this.map.on('viewreset', fn);
    return () => {
      this.map.off('moveend', fn);
      this.map.off('zoomend', fn);
      this.map.off('viewreset', fn);
    };
  }

  /** Exposed for P-08: the D3 group to render arcs and dots into. */
  getLayerGroup(): SVGGElement {
    return this.g;
  }

  /** Exposed for P-08: project a lat/lng to SVG coordinates (container-point). */
  projectPoint(lat: number, lng: number): L.Point {
    return this.project([lat, lng]);
  }

  /** Pan and zoom (minimum zoom 6) to a lat/lng. Called by BrickWallPanel. */
  panTo(lat: number, lng: number): void {
    this.map.setView([lat, lng], Math.max(this.map.getZoom(), 6));
  }

  unmount(): void {
    this.map?.remove();
    super.unmount();
  }
}
